// The shape every query factory returns, and the identity that tags a key with its data type (T-023; split out
// of `queries.ts` by T-166 so the alert factories in `alerts.ts` build the same options without a cycle).

import type { keepPreviousData, DataTag, QueryKey } from '@tanstack/query-core';
import type { ApiClient } from '../api/ApiClient';
import { ApiError } from '../api/ApiError';
import type { ApiResponse } from '../types/primitives';

/** The shape every factory returns — assignable to `useQuery` / `prefetchQuery` / `ensureQueryData` options
 *  (a structural subset of `QueryObserverOptions`, proven by a type test). */
export interface SlQueryOptions<TData, TKey extends QueryKey = QueryKey> {
  queryKey: DataTag<TKey, TData, ApiError>;
  queryFn: () => Promise<TData>;
  staleTime: number;
  /** Present on every period-keyed read; always `keepPreviousData` itself. */
  placeholderData?: typeof keepPreviousData;
  /**
   * `'always'` on a DEMO client, absent (TanStack's `'online'`) on http — T-356, D-879. The demo server is
   * in-process and never needs the network, but under the default mode TanStack asks `onlineManager` before
   * every attempt and a browser that reports itself offline (a throttled pane says slow-2g) leaves a failed
   * read's retry `fetchStatus: 'paused'` for ever — so `?demoFault=error:…` sat in its loading state and no
   * REQ-287 error state was reachable by address. `always` runs the retry, which fails, which is the state.
   */
  networkMode?: 'always';
  /** `syncStatus` polls while a sync is running (REQ-002 AC2 progress). */
  refetchInterval?: (query: { state: { data?: TData } }) => number | false;
}

/** The options every factory of one `createQueries` shares: the stale time and, on a demo client, the network mode. */
export interface QueryBase {
  staleTime: number;
  networkMode?: 'always';
}

/** `QueryBase` for a client: a demo transport is in-process, so its reads never wait for the network (D-879). */
export function queryBase(transport: ApiClient['transport'], staleTime: number): QueryBase {
  return transport === 'demo' ? { staleTime, networkMode: 'always' } : { staleTime };
}

/** `queryOptions()` is not in query-core at 5.103.2, so this is that identity: it tags the key (`DataTag`). */
export function define<TData, const TKey extends QueryKey>(
  options: Omit<SlQueryOptions<TData, TKey>, 'queryKey'> & { queryKey: TKey },
): SlQueryOptions<TData, TKey> {
  return options as unknown as SlQueryOptions<TData, TKey>;
}

/**
 * A period-keyed answer must echo its period (`meta.period`, API §12.1 / §1.5). One without is MALFORMED, not slow
 * (D-892's amendment, REQ-280 AC2 v2.8): the read rejects with the client-side `malformed_answer`, so the surface
 * takes its declared error state (REQ-287) and never a permanent skeleton — "a rule that can produce one is not
 * allowed to be the rule". Every period-keyed factory in `queries.ts` chains it; `?demoFault=period:missing` serves
 * such an answer on a demo build, which is how the state is reached by address (T-364).
 */
export function requirePeriod<T extends ApiResponse<unknown>>(answer: T): T {
  const period = answer.meta?.period;
  if (!period || typeof period.since !== 'string' || typeof period.until !== 'string') {
    throw new ApiError('malformed_answer');
  }
  return answer;
}
